import { createContext, useCallback, useContext, useRef, useState } from "react";

/**
 * CTO Directive #122.1.3 — Living Orb reads verified SSE only.
 *
 * This context exposes a `runVoiceTurn` function that POSTs to
 * `/api/me/work/voice-turn/stream`, consumes the SSE feed and
 * updates the current orb phase + Turkish label + honest text.
 * The Living Orb subscribes to this state via `useWorkStream()`.
 *
 * No local guess, no synthesised animation state — the Orb only
 * moves when the backend confirms a phase.
 */

const INITIAL = {
  phase: null,               // one of the 9 CTO phases
  orbLabelTr: "",            // short verified label from the server
  textTr: "",                // full honest sentence from the server
  verified: false,
  verificationMode: "none",  // real | simulated | none
  pendingChangeId: null,
  workSessionId: null,
  lastEventAt: null,
  running: false,
  events: [],                // debug: last 20 raw envelopes
};

const WorkStreamContext = createContext({
  state: INITIAL,
  runVoiceTurn: async () => {},
  reset: () => {},
});

export function WorkStreamProvider({ children }) {
  const [state, setState] = useState(INITIAL);
  const abortRef = useRef(null);

  const reset = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = null;
    setState(INITIAL);
  }, []);

  const runVoiceTurn = useCallback(async (body) => {
    if (abortRef.current) abortRef.current.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setState((s) => ({ ...INITIAL, running: true,
                        workSessionId: body.work_session_id || null }));

    const token = localStorage.getItem("mios_token") || "";
    const base = process.env.REACT_APP_BACKEND_URL || "";
    let res;
    try {
      res = await fetch(`${base}/api/me/work/voice-turn/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify(body),
        signal: ac.signal,
      });
    } catch (_) {
      setState((s) => ({ ...s, running: false,
                          phase: "failed_honestly",
                          orbLabelTr: "",
                          textTr: "Ses akışını başlatamadım." }));
      return;
    }
    if (!res.ok || !res.body) {
      setState((s) => ({ ...s, running: false,
                          phase: "failed_honestly",
                          orbLabelTr: "",
                          textTr: "Yanıt akışı açılmadı." }));
      return;
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder("utf-8");
    let buffer = "";
    while (true) {
      let chunk;
      try {
        chunk = await reader.read();
      } catch (_) { break; }
      if (chunk.done) break;
      buffer += decoder.decode(chunk.value, { stream: true });
      const parts = buffer.split("\n\n");
      buffer = parts.pop() || "";
      for (const part of parts) {
        if (!part.startsWith("data:")) continue;
        let ev;
        try { ev = JSON.parse(part.slice(5).trim()); }
        catch (_) { continue; }
        setState((s) => ({
          ...s,
          phase: ev.phase,
          orbLabelTr: ev.orb_label_tr || "",
          textTr: ev.text_tr || "",
          verified: !!ev.verified,
          verificationMode: ev.work_verification_mode || "none",
          pendingChangeId: ev.pending_change_id || s.pendingChangeId,
          workSessionId: ev.work_session_id || s.workSessionId,
          lastEventAt: ev.at || null,
          events: [...s.events.slice(-19), ev],
        }));
      }
    }
    setState((s) => ({ ...s, running: false }));
  }, []);

  return (
    <WorkStreamContext.Provider value={{ state, runVoiceTurn, reset }}>
      {children}
    </WorkStreamContext.Provider>
  );
}

export function useWorkStream() {
  return useContext(WorkStreamContext);
}
